import { useIsVisible } from '@folklore/hooks';
import classNames from 'classnames';
import { useEffect, useState } from 'react';

import useCollectionsInfinite from '../../hooks/useCollectionsInfinite';
import useDocumentsInfinite, {
    usePopularDocumentsInfinite,
} from '../../hooks/useDocumentsInfinite';

import Blocks from '../blocks/Blocks';

import styles from '../../styles/lists/infinite-items-blocks.module.css';

const hooks = {
    popular_documents: usePopularDocumentsInfinite,
    collections: useCollectionsInfinite,
    documents: useDocumentsInfinite,
};

type Item = Record<string, unknown>;

type InfiniteItemsBlock<T = Item> = Block & {
    count?: number | null;
    sidebarCount?: number | null;
    separator?: boolean;
    withSeparator?: boolean | null;
    infiniteIndex?: number | null;
    itemsType?: string;
    cardType?: string;
    cardTheme?: CardTheme | LegacyCardTheme;
    cardRatio?: string;
    cardSize?: string;
    cardWidth?: number;
    cardWithout?: string[];
    cardWith?: string[];
    items?: T[] | null;
    withoutLoader?: boolean;
    cardImageLoading?: string | null;
};

export interface InfiniteItemsBlocksProps<T> {
    blocks?: InfiniteItemsBlock<T>[] | null;
    itemsBlocks?: InfiniteItemsBlock<T>[];
    separatorBlocks?: InfiniteItemsBlock<T>[];
    infiniteSeparators?: boolean;
    hook?: string | null;
    query?: Record<string, unknown> | null;
    itemsType?: string;
    cardType?: string;
    cardTheme?: CardTheme | LegacyCardTheme;
    cardRatio?: string;
    cardSize?: string;
    cardWidth?: number;
    cardWithout?: string[];
    cardWith?: string[];
    blockProps?: Record<string, unknown> | null;
    disabled?: boolean;
    withoutMaxWidth?: boolean;
    className?: string | null;
    blocksClassName?: string | null;
}

const getCountFromBlock = ({ count = 6, sidebarCount = null }: InfiniteItemsBlock) =>
    count || (sidebarCount !== null ? sidebarCount + 1 : 0);

const defaultItemsBlocks = [
    {
        type: 'items',
        itemsType: 'documents',
        presentation: 'grid',
        cardWidth: 220,
        cardTheme: 'box-translucent' as const,
        cardRatio: 'horizontal',
        cardSize: 'responsive',
        count: 18,
        minColumns: 2,
    },
];

const defaultSeparatorBlocks = [
    {
        type: 'ad',
        slot: 'fullwidth',
    },
];

function InfiniteItemsBlocks<T = DocumentSnippet>({
    blocks: initialBlocks = null,
    itemsBlocks = defaultItemsBlocks,
    separatorBlocks = defaultSeparatorBlocks,
    infiniteSeparators = true,
    hook = null,
    query = null,
    itemsType = 'documents',
    cardType,
    cardTheme,
    cardRatio,
    cardSize,
    cardWithout,
    cardWith,
    cardWidth,
    blockProps = null,
    withoutMaxWidth = false,
    disabled = false,
    className = null,
}: InfiniteItemsBlocksProps<T>) {
    const [blocks, setBlocks] = useState<InfiniteItemsBlock<T>[]>(initialBlocks || []);
    const [separatorBlockIndex, setSeparatorIndex] = useState(0);
    const { ref: endOfPageRef, visible: endIsVisible } = useIsVisible({
        rootMargin: '50%',
    });

    useEffect(() => {
        if (blocks !== initialBlocks) {
            setBlocks(initialBlocks || []);
            setSeparatorIndex(0);
        }
    }, [initialBlocks]);

    const {
        pages = null,
        isFetching = false,
        isFetchingNextPage = false,
        isInitialLoading = false,
        fetchNextPage,
    } = (hook !== null ? hooks[hook] : hooks.documents)(
        {
            ...query,
        },
        {
            enabled: !disabled,
            withOffset: true,
        },
    );

    const items =
        pages !== null
            ? pages.reduce<T[]>(
                  (allItems, { data: pageItems = [], recommendationId = null }) => [
                      ...allItems,
                      ...(recommendationId !== null
                          ? pageItems.map((it) => ({ ...it, recommendationId }))
                          : pageItems),
                  ],
                  [],
              )
            : [];

    const total = (pages || []).reduce<number | null>(
        (currentTotal, { pagination: { total: newTotal = 0 } = {} }) =>
            currentTotal === null || newTotal > currentTotal ? newTotal : currentTotal,
        null,
    );

    const blockItemsCount = blocks
        .filter(({ separator = false }) => !separator)
        .reduce((acc, block) => acc + getCountFromBlock(block), 0);
    const fetchCount = blockItemsCount - items.length;
    const endIsReached = total !== null && total <= blockItemsCount;

    useEffect(() => {
        if (!disabled) {
            setBlocks(initialBlocks || []);
        }
    }, [disabled]);

    useEffect(() => {
        if (
            !disabled &&
            fetchCount > 0 &&
            !isFetching &&
            !isFetchingNextPage &&
            !isInitialLoading
        ) {
            fetchNextPage();
        }
    }, [disabled, fetchCount, isFetching, isFetchingNextPage, isInitialLoading]);

    useEffect(() => {
        if (
            !endIsVisible ||
            endIsReached ||
            isFetchingNextPage ||
            (isFetching && !isInitialLoading) ||
            (isInitialLoading && blocks.length === (initialBlocks || []).length) ||
            disabled
        ) {
            return;
        }

        const separatorBlock =
            infiniteSeparators || separatorBlockIndex < separatorBlocks.length
                ? separatorBlocks[separatorBlockIndex % separatorBlocks.length] || null
                : null;
        const hasSeparator = separatorBlock !== null;
        const availableBlocks = itemsBlocks
            .map((block, index) => ({
                ...block,
                infiniteIndex: index,
            }))
            .filter(
                ({ withSeparator = null }) =>
                    withSeparator === null || hasSeparator === withSeparator,
            );
        const { infiniteIndex: lastInfiniteBlockIndex = null } =
            [...blocks].reverse().find(({ infiniteIndex = null }) => infiniteIndex !== null) || {};
        const lastRelativeIndex = availableBlocks.findIndex(
            ({ infiniteIndex }) => infiniteIndex === lastInfiniteBlockIndex,
        );
        const nextInfiniteBlockIndex =
            lastRelativeIndex !== -1 && lastRelativeIndex < availableBlocks.length - 1
                ? lastRelativeIndex + 1
                : 0;
        const newBlock = availableBlocks[nextInfiniteBlockIndex] || null;

        setBlocks(
            [
                ...blocks,
                newBlock !== null
                    ? {
                          id: `infinite-${blocks.length}`,
                          ...newBlock,
                      }
                    : null,
                separatorBlock !== null && newBlock !== null
                    ? {
                          id: `separator-${blocks.length + 1}`,
                          ...separatorBlock,
                          separator: true,
                      }
                    : null,
            ].filter((it): it is InfiniteItemsBlock<T> => it !== null),
        );

        setSeparatorIndex(separatorBlockIndex + 1);
    }, [
        disabled,
        endIsVisible,
        endIsReached,
        isFetching,
        isFetchingNextPage,
        isInitialLoading,
        pages,
        blocks.length,
        infiniteSeparators,
    ]);

    const { blocks: blocksWithItems } = blocks.reduce(
        ({ blocks: currentBlocks, items: currentItems }, block, index) => {
            const { count = 6, sidebarCount = null, separator = false } = block;
            const finalCount = count || (sidebarCount !== null ? sidebarCount + 1 : 0);

            if (separator) {
                return {
                    blocks: [
                        ...currentBlocks,
                        {
                            cardImageLoading: index > 0 ? 'lazy' : null,
                            ...blockProps,
                            ...block,
                        },
                    ],
                    items: currentItems,
                };
            }

            const blockItems = currentItems.slice(0, finalCount);

            return !endIsReached || blockItems.length > 0
                ? {
                      blocks: [
                          ...currentBlocks,
                          {
                              cardImageLoading: index > 0 ? 'lazy' : null,
                              ...block,
                              itemsType: itemsType || block.itemsType,
                              cardType: cardType || block.cardType,
                              cardWithout: cardWithout || block.cardWithout,
                              cardWith: cardWith || block.cardWith,
                              cardTheme: cardTheme || block.cardTheme,
                              cardRatio: cardRatio || block.cardRatio,
                              cardSize: cardSize || block.cardSize,
                              cardWidth: cardWidth || block.cardWidth,
                              items: blockItems.length > 0 ? blockItems : null,
                              withoutLoader: true,
                              ...blockProps,
                          },
                      ],
                      items: currentItems.slice(finalCount),
                  }
                : {
                      blocks: currentBlocks,
                      items: currentItems,
                  };
        },
        {
            blocks: [] as InfiniteItemsBlock<T>[],
            items: items || [],
        },
    );

    const hasNotLoaded = isFetching && isInitialLoading && blocksWithItems.length === 0;

    return (
        <div className={classNames([styles.container, className])}>
            <Blocks
                blocks={
                    hasNotLoaded
                        ? [
                              {
                                  id: 'infinite-0',
                                  ...itemsBlocks[0],
                                  withoutLoader: true,
                              },
                          ]
                        : blocksWithItems
                }
                blockClassName={({ separator = false }) =>
                    classNames([
                        styles.block,
                        {
                            [styles.separator]: separator,
                        },
                    ])
                }
                withoutMaxWidth={withoutMaxWidth}
            />
            <div className={styles.endOfPage} ref={endOfPageRef} />
        </div>
    );
}

export default InfiniteItemsBlocks;
